import { useRef, useState } from 'react'
import { useAuth } from '@/api/auth'
import { ApiError, isTransportError } from '@/api/client'
import { useWarehouseCompanyCandidates, useClassifyWarehouseCandidate, type WarehouseCompanyCandidate } from '@/api/warehouse'
import { isCompanyReviewPending, warehouseErrorMessage, type WarehouseCompany } from '@/lib/warehouse-company'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

export default function PriostahlClassificationPage() {
  const { warehouse } = useAuth()
  return warehouse.lead ? <ClassificationInbox /> : <p className="p-4">Nur Lagerleiter dürfen Artikel-Zuordnungen prüfen.</p>
}

function ClassificationInbox() {
  const [offset, setOffset] = useState(0)
  const query = useWarehouseCompanyCandidates(offset)
  const mutation = useClassifyWarehouseCandidate()
  const [selected, setSelected] = useState<WarehouseCompanyCandidate | null>(null)
  const [company, setCompany] = useState<'' | WarehouseCompany>('')
  const [stockId, setStockId] = useState('')
  const [reason, setReason] = useState('')
  const [checked, setChecked] = useState(false)
  const [closed, setClosed] = useState(false)
  const [message, setMessage] = useState('')
  const busy = useRef(false)

  function choose(row: WarehouseCompanyCandidate) {
    setSelected(row); setCompany(''); setStockId(row.stock_item_id === null ? '' : String(row.stock_item_id))
    setReason(''); setChecked(false); setMessage('')
  }
  async function reload() {
    setSelected(null); setChecked(false); setCompany('')
    const result = await query.refetch()
    if (!result.isError) { setClosed(false); setMessage('') }
  }
  const parsedId = stockId.trim() === '' ? null : Number(stockId)
  const validId = parsedId === null || (Number.isSafeInteger(parsedId) && parsedId > 0)
  async function save() {
    if (!selected?.original_article || !company || !reason.trim() || !checked || !validId || closed || busy.current) return
    busy.current = true
    try {
      await mutation.mutateAsync({ id: selected.worklist_id, body: {
        stock_item_id: parsedId, company, original_article: selected.original_article,
        expected_hash: selected.expected_hash, reason: reason.trim(),
      } })
      setSelected(null); setMessage('Zuordnung gespeichert. Die Firma wird beim nächsten Lagerzugriff erneut geprüft.')
    } catch (error) {
      setClosed(true); setChecked(false)
      setMessage(error instanceof ApiError && error.status === 409
        ? 'Daten wurden inzwischen geändert. Aktuelle Daten laden und die Zuordnung erneut prüfen.'
        : isTransportError(error) && !isCompanyReviewPending(error)
          ? 'Antwort nicht bestätigt. Der Ausgang ist unklar. Aktuelle Daten laden und prüfen, bevor eine neue Entscheidung getroffen wird.'
        : warehouseErrorMessage(error))
    } finally { busy.current = false }
  }

  return <div className="p-4 max-w-6xl mx-auto space-y-4">
    <div className="flex flex-wrap justify-between items-start gap-3">
      <div><h1 className="text-xl font-bold">Artikel-Zuordnung</h1>
        <p className="text-sm text-muted">Stahlia und Priostahl · Ungeklärte Artikel bleiben gesperrt, bis ihre Firma ausdrücklich geprüft ist.</p></div>
      <Button variant="outline" disabled={query.isFetching || mutation.isPending} onClick={() => void reload()}>Aktuelle Daten laden</Button>
    </div>
    {(message || query.isError) && <p role="alert" className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">{message || warehouseErrorMessage(query.error)}</p>}
    <div className="grid gap-4 md:grid-cols-[minmax(240px,1fr)_minmax(0,2fr)]">
      <section className="rounded-xl border border-line bg-panel p-3 space-y-3" aria-label="Ungeklärte Artikel">
        <h2 className="font-semibold">{query.data?.total ?? '—'} ungeklärte Zuordnung(en)</h2>
        {query.isLoading && <p className="text-sm text-muted">Lade Zuordnungen…</p>}
        {query.data?.candidates.map(row => <button key={row.worklist_id} disabled={closed || mutation.isPending || query.isError}
          className={`block w-full text-left rounded-md border p-3 focus-visible:outline-2 focus-visible:outline-primary ${selected?.worklist_id === row.worklist_id ? 'border-primary bg-primary-soft' : 'border-line hover:bg-primary-soft'}`}
          onClick={() => choose(row)}>
          <span className="font-mono text-sm">#{row.worklist_id} · {row.original_article ?? 'Original fehlt'}</span>
          <span className="block text-xs text-amber-700">Firma ungeklärt</span>
        </button>)}
        {query.data?.total === 0 && <p className="text-sm text-muted">Keine ungeklärten Zuordnungen.</p>}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={offset === 0 || mutation.isPending || closed} onClick={() => { setSelected(null); setOffset(offset - 100) }}>Zurück</Button>
          <Button size="sm" variant="outline" disabled={!query.data || offset + 100 >= query.data.total || mutation.isPending || closed} onClick={() => { setSelected(null); setOffset(offset + 100) }}>Weitere</Button>
        </div>
      </section>
      {selected ? <section className="rounded-xl border border-line border-t-4 border-t-amber-500 p-4 space-y-4" aria-label="Zuordnung prüfen">
        <div><div className="text-xs font-semibold uppercase text-amber-700">Original → Verknüpfung → Firma</div>
          <h2 className="font-mono text-lg font-semibold">#{selected.worklist_id} · {selected.original_article ?? 'Original fehlt'}</h2>
          <p className="text-sm text-muted">Das Original bleibt unverändert. Nur dieser ausgewählte Datensatz wird zur Prüfung gesendet.</p></div>
        <div className="text-sm"><b>Grund der Prüfung: </b>{String((selected.evidence['candidate'] as Record<string, unknown> | null)?.['reason'] ?? 'Firma anhand der aktuellen Verknüpfungen nicht eindeutig.')}</div>
        <details open><summary className="text-sm font-semibold cursor-pointer">Aktuelle Verknüpfungen und Originaldaten</summary>
          <pre className="mt-2 max-h-72 overflow-auto rounded-md bg-panel p-3 text-xs font-mono">{JSON.stringify(selected.evidence, null, 2)}</pre></details>
        <div><div className="text-xs text-muted">Prüfstand (Zuordnung)</div><code className="block break-all text-xs">{selected.expected_hash}</code></div>
        <fieldset disabled={mutation.isPending || closed || query.isError} className="space-y-3">
          <label className="block text-sm">Plattform-Artikel-ID (leer = ohne Verknüpfung)
            <Input aria-label="Plattform-Artikel-ID (leer = ohne Verknüpfung)" inputMode="numeric" value={stockId} onChange={e => { setStockId(e.target.value); setChecked(false) }} /></label>
          <label className="block text-sm">Firma festlegen
            <select aria-label="Firma festlegen" className="block w-full rounded-md border border-line bg-panel p-2 focus-visible:outline-2 focus-visible:outline-primary" value={company} onChange={e => { setCompany(e.target.value as '' | WarehouseCompany); setChecked(false) }}>
              <option value="">Bitte ausdrücklich wählen</option><option value="stahlia">Stahlia</option><option value="priostahl">Priostahl</option>
            </select></label>
          <label className="block text-sm">Begründung<Textarea aria-label="Begründung" maxLength={2000} value={reason} onChange={e => { setReason(e.target.value); setChecked(false) }} /></label>
          <label className="flex gap-2 items-start text-sm"><input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} />Zuordnung, Original und Verknüpfungen geprüft</label>
          <Button disabled={!company || !reason.trim() || !checked || !validId || !selected.original_article} onClick={() => void save()}>{mutation.isPending ? 'Speichere…' : 'Zuordnung speichern'}</Button>
        </fieldset>
      </section> : <p className="text-sm text-muted p-4">Artikel links auswählen, Original und aktuelle Verknüpfungen prüfen, dann Firma ausdrücklich festlegen.</p>}
    </div>
  </div>
}
